import { useState, useEffect, useRef } from 'react';
import { supplierReconAPI } from '../lib/api';
import {
  FiPackage, FiPlus, FiTrash2, FiLoader, FiAlertCircle, FiCheck,
  FiCloud, FiMail, FiFolder, FiToggleLeft, FiToggleRight, FiDatabase,
  FiChevronDown, FiChevronRight, FiChevronUp, FiEdit2, FiX, FiUpload, FiDownload,
} from 'react-icons/fi';
import SupplierFolderBrowser from './SupplierFolderBrowser';

const SUPPLIER_DOC_TYPES = [
  { value: 'supplier_invoices', label: 'Supplier Invoices' },
  { value: 'supplier_statements', label: 'Supplier Statements' },
  { value: 'company_supplier_ledgers', label: 'Company Supplier Ledgers' },
  { value: 'supplier_payments', label: 'Supplier Payments' },
  { value: 'payment_files', label: 'Payment Files' },
  { value: 'purchase_listing', label: 'Purchase Listings' },
  { value: 'supplier_payout', label: 'Payouts' },
  { value: 'pos_cashup', label: 'Cashups' },
  { value: 'pos_day_end', label: 'POS Day End Reports' },
  { value: 'purchase_order', label: 'Purchase Orders' },
];

const DOC_TYPE_PLACEHOLDERS = {
  supplier_invoices: 'Itemised tax invoice from supplier for goods or services delivered',
  supplier_statements: 'Monthly account statement showing all transactions and closing balance',
  company_supplier_ledgers: 'Internal ERP/SAP creditors ledger showing all transactions per supplier',
  supplier_payments: 'Remittance advice or EFT payment confirmation sent to supplier',
  payment_files: 'Batch payment file or multi-supplier payment export',
  purchase_listing: 'Point-of-sale supplier purchase listing for the day, week or month',
  supplier_payout: 'Point-of-sale payout to supplier via the till for the day, week or month',
  pos_cashup: 'Point-of-sale cashup report for the day, week or month',
  pos_day_end: 'POS day-end Z report with total sales, cash payouts, tender types, dept analysis and waiter commissions',
  purchase_order: 'Purchase order issued to supplier for goods or services to be delivered',
};

// Shared focus/hit-area classes for the Aliases and Accounts blocks
const FOCUS_RING = 'rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const LINK_CLS = `inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800 transition-colors min-h-[44px] md:min-h-0 ${FOCUS_RING}`;
const INPUT_CLS = 'px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500';
const ROW_ICON_BTN = `opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100 p-2.5 md:p-1 transition-opacity flex-shrink-0 disabled:opacity-50 ${FOCUS_RING}`;

// Neutralise spreadsheet formula injection (=, +, -, @, tab, CR) and escape quotes
const csvCell = (v) => {
  let s = String(v ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
};

// Builds the post-import summary line, omitting zero segments
function formatImportResult({ inserted = 0, updated = 0, deleted = 0, unchanged = 0, invalid = 0, duplicates = 0 }) {
  const done = [];
  if (inserted) done.push(`${inserted} added`);
  if (updated) done.push(`${updated} updated`);
  if (deleted) done.push(`${deleted} deleted in Replace`);
  const notChanged = [];
  if (unchanged) notChanged.push(`${unchanged} unchanged`);
  if (invalid) notChanged.push(`${invalid} invalid`);
  if (duplicates) notChanged.push(`${duplicates} duplicate${duplicates === 1 ? '' : 's'}`);
  const total = unchanged + invalid + duplicates;
  let msg = `Import complete: ${done.length ? done.join(', ') : 'no changes'}.`;
  if (total) msg += ` ${total} not changed (${notChanged.join(', ')}).`;
  return msg;
}

function typeLabel(value) {
  return SUPPLIER_DOC_TYPES.find(t => t.value === value)?.label || value;
}

function typeColor(value) {
  const map = {
    supplier_invoices: 'bg-blue-100 text-blue-700',
    supplier_statements: 'bg-purple-100 text-purple-700',
    company_supplier_ledgers: 'bg-yellow-100 text-yellow-800',
    supplier_payments: 'bg-green-100 text-green-700',
    payment_files: 'bg-orange-100 text-orange-700',
    purchase_listing: 'bg-teal-100 text-teal-700',
    supplier_payout: 'bg-pink-100 text-pink-700',
    pos_cashup: 'bg-indigo-100 text-indigo-700',
    pos_day_end: 'bg-rose-100 text-rose-700',
  };
  return map[value] || 'bg-gray-100 text-gray-700';
}

function mappingPath(m) {
  if (m.source === 'sharepoint') {
    return m.folder_path || [m.site_name, m.drive_name, m.folder_name].filter(Boolean).join(' / ');
  }
  if (m.source === 'doxolve') {
    return m.folder_path || m.folder_name || 'Root';
  }
  return [m.mailbox_email, m.mail_folder_name].filter(Boolean).join(' / ');
}

function SourceIcon({ source }) {
  if (source === 'sharepoint') return <FiCloud className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />;
  if (source === 'doxolve') return <FiDatabase className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />;
  return <FiMail className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />;
}

function DeleteGroupConfirm({ group, onConfirm, onCancel, deleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-red-600">
            <FiTrash2 className="w-4 h-4" />
            <h2 className="text-sm font-semibold">Delete Group</h2>
          </div>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600">
            <FiX className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-700">
            Delete group <span className="font-semibold">"{group.name}"</span>?
            {group.folder_mappings?.length > 0 && (
              <> This will remove all {group.folder_mappings.length} folder mapping{group.folder_mappings.length !== 1 ? 's' : ''} in this group.</>
            )}
          </p>
          <p className="text-xs text-gray-500">Existing recon sessions will not be affected.</p>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onCancel} disabled={deleting} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {deleting ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiTrash2 className="w-3.5 h-3.5" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SupplierReconSettings() {
  const [enabled, setEnabled] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Document type descriptions (keyed by doc type value)
  const [docTypeDescriptions, setDocTypeDescriptions] = useState({});
  const [savingDocTypes, setSavingDocTypes] = useState(false);
  const [editingDocTypes, setEditingDocTypes] = useState(false);
  const [docTypeEdits, setDocTypeEdits] = useState({});

  // New group form
  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Expanded groups
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Add folder browser: { groupId }
  const [addFolderToGroup, setAddFolderToGroup] = useState(null);

  // Edit group name/description inline
  const [editingGroup, setEditingGroup] = useState(null); // { id, name, description }
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirmation
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);
  const [deletingGroupId, setDeletingGroupId] = useState(null);

  // Custom doc types per group: { [groupId]: { adding: bool, newName: str, newDesc: str, saving: bool } }
  const [customTypeState, setCustomTypeState] = useState({});

  // Supplier aliases per group: { [groupId]: alias[] }
  const [aliasesState, setAliasesState] = useState({});
  // New alias form state per group: { [groupId]: { adding: bool, rawName: str, canonicalName: str, saving: bool } }
  const [aliasFormState, setAliasFormState] = useState({});

  const getAliasForm = (groupId) => aliasFormState[groupId] || { adding: false, rawName: '', canonicalName: '', saving: false };
  const setAliasFormField = (groupId, field, value) =>
    setAliasFormState(prev => ({ ...prev, [groupId]: { ...getAliasForm(groupId), [field]: value } }));

  // Load-failure and row-level delete errors per group
  const [aliasLoadError, setAliasLoadError] = useState({});
  const [aliasRowError, setAliasRowError] = useState({}); // { [groupId]: { id, message } | null }

  const loadAliases = async (groupId) => {
    setAliasLoadError(prev => ({ ...prev, [groupId]: false }));
    try {
      const res = await supplierReconAPI.getAliases(groupId);
      setAliasesState(prev => ({ ...prev, [groupId]: res.data.aliases || [] }));
    } catch {
      setAliasLoadError(prev => ({ ...prev, [groupId]: true }));
    }
  };

  const handleAddAlias = async (groupId) => {
    const form = getAliasForm(groupId);
    if (!form.rawName.trim() || !form.canonicalName.trim()) return;
    setAliasFormField(groupId, 'saving', true);
    try {
      const res = await supplierReconAPI.createAlias(groupId, {
        raw_name: form.rawName.trim(),
        canonical_name: form.canonicalName.trim(),
        source: 'manual',
      });
      setAliasesState(prev => ({ ...prev, [groupId]: [res.data.alias, ...(prev[groupId] || [])] }));
      setAliasFormState(prev => ({ ...prev, [groupId]: { adding: false, rawName: '', canonicalName: '', saving: false } }));
    } catch (err) {
      setAliasFormState(prev => ({ ...prev, [groupId]: { ...getAliasForm(groupId), ...prev[groupId], saving: false, error: err.response?.data?.error || 'Failed to add alias' } }));
    }
  };

  const handleDeleteAlias = async (groupId, alias) => {
    if (!window.confirm(`Delete alias "${alias.raw_name}"?`)) return;
    setAliasRowError(prev => ({ ...prev, [groupId]: null }));
    try {
      await supplierReconAPI.deleteAlias(groupId, alias.id);
      setAliasesState(prev => ({ ...prev, [groupId]: (prev[groupId] || []).filter(a => a.id !== alias.id) }));
    } catch (err) {
      setAliasRowError(prev => ({ ...prev, [groupId]: { id: alias.id, message: err.response?.data?.error || 'Failed to delete alias' } }));
    }
  };

  // Set of accounts per group: { [groupId]: account[] }
  const [accountsState, setAccountsState] = useState({});
  // Add-account form per group: { [groupId]: { adding, code, description, type, saving, error } }
  const [accountFormState, setAccountFormState] = useState({});
  // Inline edit per group: { [groupId]: { id, code, description, type, saving, error } | null }
  const [accountEditState, setAccountEditState] = useState({});
  // Search text per group
  const [accountSearch, setAccountSearch] = useState({});
  // Import flow per group: { [groupId]: { uploading, preview, mode, committing, result, error } }
  const [accountImportState, setAccountImportState] = useState({});
  const accountFileInputs = useRef({});

  const ACCOUNT_TYPE_SUGGESTIONS = ['Trading Income', 'Trading Expense', 'Income', 'Expense'];
  const EMPTY_ACCOUNT_FORM = { adding: false, code: '', description: '', type: '', saving: false, error: '' };

  const getAccountForm = (groupId) => accountFormState[groupId] || EMPTY_ACCOUNT_FORM;
  const setAccountFormFields = (groupId, patch) =>
    setAccountFormState(prev => ({ ...prev, [groupId]: { ...(prev[groupId] || EMPTY_ACCOUNT_FORM), ...patch } }));
  const getAccountImport = (groupId) => accountImportState[groupId] || { uploading: false, preview: null, mode: 'merge', committing: false, result: '', error: '' };
  const setAccountImportFields = (groupId, patch) =>
    setAccountImportState(prev => ({ ...prev, [groupId]: { ...getAccountImport(groupId), ...(prev[groupId] || {}), ...patch } }));

  // Load-failure and row-level delete errors per group
  const [accountLoadError, setAccountLoadError] = useState({});
  const [accountRowError, setAccountRowError] = useState({}); // { [groupId]: { id, message } | null }

  const loadAccounts = async (groupId) => {
    setAccountLoadError(prev => ({ ...prev, [groupId]: false }));
    try {
      const res = await supplierReconAPI.getAccounts(groupId);
      setAccountsState(prev => ({ ...prev, [groupId]: res.data.accounts || [] }));
    } catch {
      setAccountLoadError(prev => ({ ...prev, [groupId]: true }));
    }
  };

  const handleAddAccount = async (groupId) => {
    const form = getAccountForm(groupId);
    if (!form.code.trim()) return;
    setAccountFormFields(groupId, { saving: true, error: '' });
    try {
      await supplierReconAPI.createAccount(groupId, {
        account_code: form.code.trim(),
        description: form.description.trim(),
        account_type: form.type.trim(),
      });
      await loadAccounts(groupId);
      setAccountFormState(prev => ({ ...prev, [groupId]: EMPTY_ACCOUNT_FORM }));
    } catch (err) {
      setAccountFormFields(groupId, { saving: false, error: err.response?.data?.error || 'Failed to add account' });
    }
  };

  const handleSaveAccountEdit = async (groupId) => {
    const edit = accountEditState[groupId];
    if (!edit || !edit.code.trim()) return;
    setAccountEditState(prev => ({ ...prev, [groupId]: { ...edit, saving: true, error: '' } }));
    try {
      const res = await supplierReconAPI.updateAccount(groupId, edit.id, {
        account_code: edit.code.trim(),
        description: edit.description.trim(),
        account_type: edit.type.trim(),
      });
      setAccountsState(prev => ({
        ...prev,
        [groupId]: (prev[groupId] || []).map(a => a.id === edit.id ? res.data.account : a)
          .sort((a, b) => a.account_code < b.account_code ? -1 : a.account_code > b.account_code ? 1 : 0),
      }));
      setAccountEditState(prev => ({ ...prev, [groupId]: null }));
    } catch (err) {
      setAccountEditState(prev => ({ ...prev, [groupId]: { ...edit, saving: false, error: err.response?.data?.error || 'Failed to save account' } }));
    }
  };

  const handleDeleteAccount = async (groupId, account) => {
    if (!window.confirm(`Delete account ${account.account_code}?`)) return;
    setAccountRowError(prev => ({ ...prev, [groupId]: null }));
    try {
      await supplierReconAPI.deleteAccount(groupId, account.id);
      setAccountsState(prev => ({ ...prev, [groupId]: (prev[groupId] || []).filter(a => a.id !== account.id) }));
    } catch (err) {
      setAccountRowError(prev => ({ ...prev, [groupId]: { id: account.id, message: err.response?.data?.error || 'Failed to delete account' } }));
    }
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadAccountTemplate = async (groupId) => {
    try {
      const res = await supplierReconAPI.downloadAccountTemplate(groupId);
      triggerBlobDownload(res.data, 'account-list-template.xlsx');
    } catch {
      setAccountImportFields(groupId, { error: 'Failed to download template' });
    }
  };

  const handleExportAccountsCsv = (group) => {
    const lines = [['Account', 'Description', 'Account Type'].map(csvCell).join(',')];
    (accountsState[group.id] || []).forEach(a => lines.push([a.account_code, a.description, a.account_type].map(csvCell).join(',')));
    // UTF-8 BOM so Excel opens it cleanly
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const safeName = (group.name || 'group').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
    triggerBlobDownload(blob, `accounts-${safeName || 'group'}.csv`);
  };

  const handleAccountFileSelected = async (groupId, file) => {
    if (!file) return;
    setAccountImportFields(groupId, { uploading: true, preview: null, result: '', error: '' });
    try {
      const res = await supplierReconAPI.previewAccountImport(groupId, file);
      setAccountImportFields(groupId, { uploading: false, preview: res.data, mode: 'merge' });
    } catch (err) {
      setAccountImportFields(groupId, { uploading: false, error: err.response?.data?.error || 'Failed to read the file' });
    }
  };

  // Rows the commit will send (new / update / unchanged)
  const getImportRows = (groupId) =>
    (getAccountImport(groupId).preview?.rows || []).filter(r => ['new', 'update', 'unchanged'].includes(r.status));

  // Existing accounts that Replace would delete (not present in the file).
  // Returns null while the account list is still loading or failed to load, so a false 0 is never shown.
  const getReplaceRemoved = (groupId) => {
    if (accountsState[groupId] === undefined || accountLoadError[groupId]) return null;
    const fileCodes = new Set(getImportRows(groupId).map(r => r.account_code));
    return (accountsState[groupId] || []).filter(a => !fileCodes.has(a.account_code)).length;
  };

  const handleCommitAccountImport = async (groupId) => {
    const imp = getAccountImport(groupId);
    if (!imp.preview) return;
    const rows = getImportRows(groupId);
    if (imp.mode === 'replace') {
      const removed = getReplaceRemoved(groupId);
      if (removed === null) {
        setAccountImportFields(groupId, { error: 'Replace is unavailable until the account list has loaded. Use Merge or retry loading.' });
        return;
      }
      const groupName = groups.find(g => g.id === groupId)?.name || 'this group';
      if (!window.confirm(`Replace will permanently delete ${removed} existing account${removed === 1 ? '' : 's'} in "${groupName}" that are not in the file. Continue?`)) return;
    }
    setAccountImportFields(groupId, { committing: true, error: '', result: '' });
    try {
      const res = await supplierReconAPI.commitAccountImport(groupId, imp.mode, rows);
      const { inserted, updated, deleted, unchanged, skipped } = res.data;
      await loadAccounts(groupId);
      // Rows filtered out by the preview (invalid / in-file duplicates) were never sent, so add their counts back
      const summary = imp.preview.summary;
      setAccountImportFields(groupId, {
        committing: false,
        preview: null,
        result: formatImportResult({
          inserted,
          updated,
          deleted,
          unchanged: unchanged ?? skipped,
          invalid: (res.data.invalid || 0) + summary.invalid,
          duplicates: (res.data.duplicates || 0) + summary.duplicate_in_file,
        }),
      });
    } catch (err) {
      setAccountImportFields(groupId, { committing: false, error: err.response?.data?.error || 'Import failed' });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [configRes, groupsRes] = await Promise.all([
        supplierReconAPI.getConfig(),
        supplierReconAPI.getGroups(),
      ]);
      setEnabled(configRes.data.enabled || false);
      setGroups(groupsRes.data.groups || []);
      setDocTypeDescriptions(configRes.data.doc_type_descriptions || {});
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const showSuccessMsg = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleToggleEnabled = async () => {
    const newValue = !enabled;
    setEnabled(newValue);
    setSaving(true);
    try {
      await supplierReconAPI.saveConfig({ enabled: newValue });
      showSuccessMsg('Settings saved.');
    } catch (err) {
      setEnabled(!newValue);
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditDocTypes = () => {
    // Pre-populate with defaults for any type that has no saved description
    const edits = { ...DOC_TYPE_PLACEHOLDERS };
    Object.entries(docTypeDescriptions).forEach(([k, v]) => { if (v) edits[k] = v; });
    setDocTypeEdits(edits);
    setEditingDocTypes(true);
  };

  const handleCancelDocTypes = () => {
    setEditingDocTypes(false);
    setDocTypeEdits({});
  };

  const handleSaveDocTypes = async () => {
    setSavingDocTypes(true);
    try {
      const res = await supplierReconAPI.saveConfig({ doc_type_descriptions: docTypeEdits });
      setDocTypeDescriptions(res.data.doc_type_descriptions || {});
      setEditingDocTypes(false);
      setDocTypeEdits({});
      showSuccessMsg('Document type descriptions saved.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSavingDocTypes(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    try {
      const res = await supplierReconAPI.createGroup({ name: newGroupName.trim(), description: newGroupDesc.trim() || null });
      const g = res.data.group;
      setGroups(prev => [...prev, g].sort((a, b) => a.name.localeCompare(b.name)));
      setExpandedGroups(prev => new Set([...prev, g.id]));
      setNewGroupName('');
      setNewGroupDesc('');
      setShowNewGroupForm(false);
      showSuccessMsg(`Group "${g.name}" created.`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleAddMapping = async (mapping) => {
    const { groupId } = addFolderToGroup;
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const updated = [...(group.folder_mappings || []), mapping];
    try {
      const res = await supplierReconAPI.updateGroup(groupId, { folder_mappings: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
      setAddFolderToGroup(null);
      showSuccessMsg('Folder added.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setAddFolderToGroup(null);
    }
  };

  const handleRemoveMapping = async (groupId, mappingId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const updated = group.folder_mappings.filter(m => m.id !== mappingId);
    try {
      const res = await supplierReconAPI.updateGroup(groupId, { folder_mappings: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleSaveGroupEdit = async () => {
    if (!editingGroup || !editingGroup.name.trim()) return;
    setEditSaving(true);
    try {
      const res = await supplierReconAPI.updateGroup(editingGroup.id, {
        name: editingGroup.name.trim(),
        description: editingGroup.description || null,
      });
      setGroups(prev => prev.map(g => g.id === editingGroup.id ? res.data.group : g).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingGroup(null);
      showSuccessMsg('Group updated.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!confirmDeleteGroup) return;
    setDeletingGroupId(confirmDeleteGroup.id);
    try {
      await supplierReconAPI.deleteGroup(confirmDeleteGroup.id);
      setGroups(prev => prev.filter(g => g.id !== confirmDeleteGroup.id));
      setConfirmDeleteGroup(null);
      showSuccessMsg('Group deleted.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeletingGroupId(null);
    }
  };

  const getCustomTypeState = (groupId) => customTypeState[groupId] || { adding: false, newName: '', newDesc: '', saving: false };

  const setCustomTypeField = (groupId, field, value) => {
    setCustomTypeState(prev => ({ ...prev, [groupId]: { ...getCustomTypeState(groupId), ...prev[groupId], [field]: value } }));
  };

  const handleAddCustomType = async (groupId) => {
    const st = getCustomTypeState(groupId);
    const name = st.newName.trim();
    if (!name) return;
    setCustomTypeField(groupId, 'saving', true);
    try {
      const group = groups.find(g => g.id === groupId);
      const existing = group?.custom_doc_types || [];
      const newType = { id: `custom_${Date.now()}`, name, description: st.newDesc.trim() || '' };
      const updated = [...existing, newType];
      const res = await supplierReconAPI.updateGroup(groupId, { custom_doc_types: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
      setCustomTypeState(prev => ({ ...prev, [groupId]: { adding: false, newName: '', newDesc: '', saving: false } }));
      showSuccessMsg(`Custom type "${name}" added.`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setCustomTypeField(groupId, 'saving', false);
    }
  };

  const handleDeleteCustomType = async (groupId, typeId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const updated = (group.custom_doc_types || []).filter(t => t.id !== typeId);
    try {
      const res = await supplierReconAPI.updateGroup(groupId, { custom_doc_types: updated });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleToggleDocType = async (groupId, typeValue) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const excluded = [...(group.excluded_doc_types || [])];
    let priority = [...(group.doc_type_priority || [])];
    const isExcluded = excluded.includes(typeValue);
    const newExcluded = isExcluded
      ? excluded.filter(t => t !== typeValue)          // re-include
      : [...excluded, typeValue];                       // exclude + remove from priority
    if (!isExcluded) priority = priority.filter(t => t !== typeValue);
    try {
      const res = await supplierReconAPI.updateGroup(groupId, { excluded_doc_types: newExcluded, doc_type_priority: priority });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleMovePriority = async (groupId, typeValue, direction) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const excluded = group.excluded_doc_types || [];
    const allSystemTypeValues = SUPPLIER_DOC_TYPES.map(t => t.value);
    const included = allSystemTypeValues.filter(t => !excluded.includes(t));
    const saved = (group.doc_type_priority || []).filter(t => included.includes(t));
    const current = [...saved, ...included.filter(t => !saved.includes(t))];
    const idx = current.indexOf(typeValue);
    if (idx === -1) return;
    const newIdx = direction === 'up' ? Math.max(0, idx - 1) : Math.min(current.length - 1, idx + 1);
    if (newIdx === idx) return;
    const reordered = [...current];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      const res = await supplierReconAPI.updateGroup(groupId, { doc_type_priority: reordered });
      setGroups(prev => prev.map(g => g.id === groupId ? res.data.group : g));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const toggleExpand = (id) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // Load aliases when expanding a group for the first time
        if (!aliasesState[id]) loadAliases(id);
        if (!accountsState[id]) loadAccounts(id);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <FiLoader className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  // Show folder browser when adding a folder to a group
  if (addFolderToGroup) {
    return (
      <SupplierFolderBrowser
        onAdd={handleAddMapping}
        onCancel={() => setAddFolderToGroup(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FiPackage className="w-6 h-6 text-green-600" />
            Supplier Recon Module
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure recon groups and their source folder mappings
          </p>
        </div>
        <button
          onClick={handleToggleEnabled}
          disabled={saving}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
            enabled
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {enabled ? <FiToggleRight className="w-5 h-5" /> : <FiToggleLeft className="w-5 h-5" />}
          {enabled ? 'Enabled' : 'Disabled'}
          {saving && <FiLoader className="w-4 h-4 animate-spin ml-1" />}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-0.5">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <FiCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {!enabled && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
          The Supplier Recon module is currently disabled. Enable it above to activate.
        </div>
      )}

      {/* Recon Groups */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-medium text-gray-900">
            Recon Groups
            {groups.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">({groups.length})</span>
            )}
          </h3>
          <button
            onClick={() => { setShowNewGroupForm(true); setNewGroupName(''); setNewGroupDesc(''); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            New Group
          </button>
        </div>

        {/* New group inline form */}
        {showNewGroupForm && (
          <form onSubmit={handleCreateGroup} className="mb-4 border border-green-200 bg-green-50 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-green-900">New Recon Group</h4>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Group Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                placeholder="e.g. Evonik"
                required
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Description (optional)</label>
              <input
                type="text"
                value={newGroupDesc}
                onChange={e => setNewGroupDesc(e.target.value)}
                placeholder="e.g. Evonik supplier invoices and statements"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowNewGroupForm(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creatingGroup || !newGroupName.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {creatingGroup ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiPlus className="w-3.5 h-3.5" />}
                Create Group
              </button>
            </div>
          </form>
        )}

        {/* Group list */}
        {groups.length === 0 && !showNewGroupForm ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <FiFolder className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium mb-1">No recon groups yet</p>
            <p className="text-sm text-gray-500 mb-4">
              Create a group for each supplier (or set of suppliers) and add their folders to it.
            </p>
            <button
              onClick={() => setShowNewGroupForm(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Create First Group
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map(group => {
              const isExpanded = expandedGroups.has(group.id);
              const isEditing = editingGroup?.id === group.id;
              const mappings = group.folder_mappings || [];

              return (
                <div key={group.id} className="border border-gray-200 rounded-xl overflow-hidden">
                  {/* Group header */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <button
                      onClick={() => toggleExpand(group.id)}
                      className="flex-1 flex items-center gap-2 text-left"
                    >
                      {isExpanded
                        ? <FiChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        : <FiChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      }
                      {isEditing ? (
                        <div className="flex-1 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <input
                            type="text"
                            value={editingGroup.name}
                            onChange={e => setEditingGroup(prev => ({ ...prev, name: e.target.value }))}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            autoFocus
                          />
                          <input
                            type="text"
                            value={editingGroup.description || ''}
                            onChange={e => setEditingGroup(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Description (optional)"
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                          />
                          <button
                            onClick={handleSaveGroupEdit}
                            disabled={editSaving}
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {editSaving ? <FiLoader className="w-3 h-3 animate-spin" /> : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingGroup(null)}
                            className="px-2 py-1 text-gray-500 text-xs hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-gray-900">{group.name}</span>
                          {group.description && (
                            <span className="ml-2 text-xs text-gray-500">{group.description}</span>
                          )}
                        </div>
                      )}
                    </button>

                    {!isEditing && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-gray-500 mr-1">
                          {mappings.length} folder{mappings.length !== 1 ? 's' : ''}
                        </span>
                        <button
                          onClick={() => setEditingGroup({ id: group.id, name: group.name, description: group.description || '' })}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200 transition-colors"
                          title="Edit group"
                        >
                          <FiEdit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => { setAddFolderToGroup({ groupId: group.id }); setExpandedGroups(prev => new Set([...prev, group.id])); }}
                          className="p-1.5 text-gray-400 hover:text-green-600 rounded hover:bg-green-50 transition-colors"
                          title="Add folder"
                        >
                          <FiPlus className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setConfirmDeleteGroup(group)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                          title="Delete group"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Expanded: folder mappings + custom doc types */}
                  {isExpanded && (() => {
                    const customTypes = group.custom_doc_types || [];
                    const ctSt = getCustomTypeState(group.id);
                    return (
                      <div className="bg-white">
                        {mappings.length === 0 ? (
                          <div className="px-4 py-6 text-center">
                            <p className="text-sm text-gray-500 mb-3">No folders in this group yet.</p>
                            <button
                              onClick={() => setAddFolderToGroup({ groupId: group.id })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
                            >
                              <FiPlus className="w-3.5 h-3.5" />
                              Add Folder
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Group by doc type */}
                            {SUPPLIER_DOC_TYPES.map(dt => {
                              const typeMappings = mappings.filter(m => m.type === dt.value);
                              if (typeMappings.length === 0) return null;
                              return (
                                <div key={dt.value} className="border-t border-gray-100">
                                  <div className="px-4 py-1.5 bg-gray-50 flex items-center gap-2">
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${typeColor(dt.value)}`}>
                                      {dt.label}
                                    </span>
                                    <span className="text-xs text-gray-400">{typeMappings.length}</span>
                                  </div>
                                  <ul className="divide-y divide-gray-50">
                                    {typeMappings.map(m => (
                                      <li key={m.id} className="flex items-center gap-3 px-4 py-2.5 group hover:bg-gray-50 transition-colors">
                                        <SourceIcon source={m.source} />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm text-gray-800 truncate font-medium">
                                            {m.folder_name || m.mail_folder_name || 'Root'}
                                          </p>
                                          <p className="text-xs text-gray-400 truncate">{mappingPath(m)}</p>
                                        </div>
                                        <span className="text-xs text-gray-400 flex-shrink-0 capitalize">{m.source}</span>
                                        <button
                                          onClick={() => handleRemoveMapping(group.id, m.id)}
                                          className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity flex-shrink-0"
                                          title="Remove mapping"
                                        >
                                          <FiTrash2 className="w-4 h-4" />
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                            {/* Unknown type mappings */}
                            {mappings.filter(m => !SUPPLIER_DOC_TYPES.find(dt => dt.value === m.type)).map(m => (
                              <div key={m.id} className="border-t border-gray-100 flex items-center gap-3 px-4 py-2.5 group hover:bg-gray-50">
                                <SourceIcon source={m.source} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-800 truncate">{mappingPath(m)}</p>
                                  <p className="text-xs text-gray-400">{m.type}</p>
                                </div>
                                <button
                                  onClick={() => handleRemoveMapping(group.id, m.id)}
                                  className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <div className="px-4 py-2 border-t border-gray-100 flex justify-end">
                              <button
                                onClick={() => setAddFolderToGroup({ groupId: group.id })}
                                className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition-colors"
                              >
                                <FiPlus className="w-3.5 h-3.5" />
                                Add Folder
                              </button>
                            </div>
                          </>
                        )}

                        {/* Doc Type Configuration */}
                        {(() => {
                          const excluded = group.excluded_doc_types || [];
                          const saved = (group.doc_type_priority || []).filter(t => !excluded.includes(t));
                          const included = SUPPLIER_DOC_TYPES.map(t => t.value).filter(t => !excluded.includes(t));
                          // Full ordered list: saved priority first, then remaining included types
                          const orderedIncluded = [...saved, ...included.filter(t => !saved.includes(t))];
                          return (
                            <div className="border-t border-gray-200 px-4 py-3">
                              <div className="mb-2">
                                <span className="text-xs font-medium text-gray-700">Doc Type Configuration</span>
                                <span className="ml-1.5 text-xs text-gray-400">— set which types are used in recon and their priority order</span>
                              </div>
                              <div className="space-y-1">
                                {SUPPLIER_DOC_TYPES.map(dt => {
                                  const isExcluded = excluded.includes(dt.value);
                                  const priorityIdx = orderedIncluded.indexOf(dt.value);
                                  return (
                                    <div key={dt.value} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${isExcluded ? 'opacity-50 bg-gray-50' : 'bg-white'}`}>
                                      {/* Priority number */}
                                      <span className={`w-5 text-center text-xs font-medium flex-shrink-0 ${isExcluded ? 'text-gray-300' : 'text-gray-500'}`}>
                                        {isExcluded ? '—' : priorityIdx + 1}
                                      </span>
                                      {/* Move up/down */}
                                      <div className="flex flex-col gap-0 flex-shrink-0">
                                        <button
                                          disabled={isExcluded || priorityIdx === 0}
                                          onClick={() => handleMovePriority(group.id, dt.value, 'up')}
                                          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20 disabled:cursor-default"
                                          title="Move up"
                                        >
                                          <FiChevronUp className="w-3 h-3" />
                                        </button>
                                        <button
                                          disabled={isExcluded || priorityIdx === orderedIncluded.length - 1}
                                          onClick={() => handleMovePriority(group.id, dt.value, 'down')}
                                          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20 disabled:cursor-default"
                                          title="Move down"
                                        >
                                          <FiChevronDown className="w-3 h-3" />
                                        </button>
                                      </div>
                                      {/* Type badge */}
                                      <span className={`text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${typeColor(dt.value)}`}>{dt.label}</span>
                                      {/* Spacer */}
                                      <span className="flex-1" />
                                      {/* Include/Exclude toggle */}
                                      <button
                                        onClick={() => handleToggleDocType(group.id, dt.value)}
                                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors flex-shrink-0 ${
                                          isExcluded
                                            ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                                            : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                                        }`}
                                        title={isExcluded ? 'Click to include in recon' : 'Click to exclude from recon'}
                                      >
                                        {isExcluded
                                          ? <><FiX className="w-3 h-3" /> Excluded</>
                                          : <><FiCheck className="w-3 h-3" /> Included</>
                                        }
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Custom Document Types */}
                        <div className="border-t border-gray-200 px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="text-xs font-medium text-gray-700">Custom Document Types</span>
                              <span className="ml-1.5 text-xs text-gray-400">(reference-only, for this group)</span>
                            </div>
                            {!ctSt.adding && (
                              <button
                                onClick={() => setCustomTypeField(group.id, 'adding', true)}
                                className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition-colors"
                              >
                                <FiPlus className="w-3 h-3" />
                                Add Type
                              </button>
                            )}
                          </div>

                          {customTypes.length === 0 && !ctSt.adding && (
                            <p className="text-xs text-gray-400 italic">No custom types. Add types like "Delivery Note" that appear in documents but don't affect financial matching.</p>
                          )}

                          {customTypes.length > 0 && (
                            <ul className="space-y-1 mb-2">
                              {customTypes.map(ct => (
                                <li key={ct.id} className="flex items-center gap-2 py-1 group">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 flex-shrink-0">
                                    {ct.name}
                                  </span>
                                  {ct.description && (
                                    <span className="text-xs text-gray-400 flex-1 truncate">{ct.description}</span>
                                  )}
                                  <button
                                    onClick={() => handleDeleteCustomType(group.id, ct.id)}
                                    className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity flex-shrink-0"
                                    title="Remove"
                                  >
                                    <FiTrash2 className="w-3 h-3" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}

                          {ctSt.adding && (
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="text"
                                value={ctSt.newName}
                                onChange={e => setCustomTypeField(group.id, 'newName', e.target.value)}
                                placeholder="Type name (e.g. Delivery Note)"
                                autoFocus
                                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                onKeyDown={e => { if (e.key === 'Enter') handleAddCustomType(group.id); if (e.key === 'Escape') setCustomTypeField(group.id, 'adding', false); }}
                              />
                              <input
                                type="text"
                                value={ctSt.newDesc}
                                onChange={e => setCustomTypeField(group.id, 'newDesc', e.target.value)}
                                placeholder="Description (optional)"
                                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                                onKeyDown={e => { if (e.key === 'Enter') handleAddCustomType(group.id); if (e.key === 'Escape') setCustomTypeField(group.id, 'adding', false); }}
                              />
                              <button
                                onClick={() => handleAddCustomType(group.id)}
                                disabled={ctSt.saving || !ctSt.newName.trim()}
                                className="p-1.5 text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 flex-shrink-0"
                              >
                                {ctSt.saving ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiCheck className="w-3 h-3" />}
                              </button>
                              <button
                                onClick={() => setCustomTypeState(prev => ({ ...prev, [group.id]: { adding: false, newName: '', newDesc: '', saving: false } }))}
                                className="p-1.5 text-gray-400 hover:text-gray-600 flex-shrink-0"
                              >
                                <FiX className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Supplier Aliases */}
                        {(() => {
                          const aliases = aliasesState[group.id];
                          const alForm = getAliasForm(group.id);
                          const alLoadError = aliasLoadError[group.id];
                          const alRowError = aliasRowError[group.id];
                          return (
                            <div className="border-t border-gray-200 px-4 py-3">
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <div>
                                  <span className="text-xs font-medium text-gray-700">Supplier Aliases</span>
                                  <span className="ml-1.5 text-xs text-gray-500">(learned from corrections, applied automatically)</span>
                                </div>
                                {!alForm.adding && (
                                  <button
                                    onClick={() => setAliasFormField(group.id, 'adding', true)}
                                    className={LINK_CLS}
                                  >
                                    <FiPlus className="w-3 h-3" />
                                    Add manually
                                  </button>
                                )}
                              </div>

                              {aliases === undefined && !alLoadError && (
                                <p className="text-xs text-gray-500 italic">Loading aliases…</p>
                              )}
                              {alLoadError && (
                                <p role="alert" className="text-xs text-red-600 mb-2">
                                  Couldn't load aliases.{' '}
                                  <button onClick={() => loadAliases(group.id)} className={`underline text-blue-700 hover:text-blue-800 ${FOCUS_RING}`}>Retry</button>
                                </p>
                              )}
                              {aliases?.length === 0 && !alForm.adding && (
                                <p className="text-xs text-gray-500 italic">No aliases yet. Aliases are learned automatically when you correct supplier names.</p>
                              )}
                              {aliases?.length > 0 && (
                                <ul className="space-y-1 mb-2">
                                  {aliases.map(a => (
                                    <li key={a.id} className="py-1 group text-xs">
                                      <div className="flex items-center gap-2">
                                        <span className="flex-1 text-gray-500 truncate font-mono" title={a.raw_name}>{a.raw_name}</span>
                                        <span className="text-gray-500 flex-shrink-0">→</span>
                                        <span className="flex-1 text-gray-800 font-medium truncate" title={a.canonical_name}>{a.canonical_name}</span>
                                        <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-xs ${
                                          a.source === 'user_correction' ? 'bg-blue-50 text-blue-600' :
                                          a.source === 'match_rename' ? 'bg-purple-50 text-purple-600' :
                                          'bg-gray-100 text-gray-500'
                                        }`}>{a.source === 'user_correction' ? 'correction' : a.source === 'match_rename' ? 'rename' : 'manual'}</span>
                                        {a.usage_count > 0 && (
                                          <span className="flex-shrink-0 text-gray-500">×{a.usage_count}</span>
                                        )}
                                        <button
                                          onClick={() => handleDeleteAlias(group.id, a)}
                                          aria-label={`Delete alias ${a.raw_name}`}
                                          className={`${ROW_ICON_BTN} text-red-600 hover:text-red-700`}
                                          title="Delete alias"
                                        >
                                          <FiTrash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                      {alRowError?.id === a.id && <p role="alert" className="text-xs text-red-600 mt-1">{alRowError.message}</p>}
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {alForm.adding && (
                                <div className="mt-1">
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={alForm.rawName}
                                      onChange={e => setAliasFormField(group.id, 'rawName', e.target.value)}
                                      placeholder="AI extracted name (raw)"
                                      aria-label="AI extracted name (raw)"
                                      autoFocus
                                      className={`flex-1 ${INPUT_CLS}`}
                                      onKeyDown={e => { if (e.key === 'Enter') handleAddAlias(group.id); if (e.key === 'Escape') setAliasFormField(group.id, 'adding', false); }}
                                    />
                                    <span className="text-gray-500 text-xs flex-shrink-0">→</span>
                                    <input
                                      type="text"
                                      value={alForm.canonicalName}
                                      onChange={e => setAliasFormField(group.id, 'canonicalName', e.target.value)}
                                      placeholder="Correct canonical name"
                                      aria-label="Correct canonical name"
                                      className={`flex-1 ${INPUT_CLS}`}
                                      onKeyDown={e => { if (e.key === 'Enter') handleAddAlias(group.id); if (e.key === 'Escape') setAliasFormField(group.id, 'adding', false); }}
                                    />
                                    <button
                                      onClick={() => handleAddAlias(group.id)}
                                      disabled={alForm.saving || !alForm.rawName.trim() || !alForm.canonicalName.trim()}
                                      aria-label="Save alias"
                                      title="Save alias"
                                      className={`p-1.5 text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex-shrink-0 ${FOCUS_RING}`}
                                    >
                                      {alForm.saving ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiCheck className="w-3 h-3" />}
                                    </button>
                                    <button
                                      onClick={() => setAliasFormState(prev => ({ ...prev, [group.id]: { adding: false, rawName: '', canonicalName: '', saving: false } }))}
                                      aria-label="Cancel add alias"
                                      title="Cancel add alias"
                                      className={`p-1.5 text-gray-500 hover:text-gray-700 flex-shrink-0 ${FOCUS_RING}`}
                                    >
                                      <FiX className="w-3 h-3" />
                                    </button>
                                  </div>
                                  {alForm.error && <p role="alert" className="text-xs text-red-600 mt-1">{alForm.error}</p>}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Set of Accounts */}
                        {(() => {
                          const accounts = accountsState[group.id];
                          const acForm = getAccountForm(group.id);
                          const acEdit = accountEditState[group.id];
                          const acRowError = accountRowError[group.id];
                          const acLoadError = accountLoadError[group.id];
                          const imp = getAccountImport(group.id);
                          const busy = imp.committing;
                          const scrollable = accounts?.length > 8;
                          // The search box only exists when the list is scrollable; never filter by hidden text
                          const searchRaw = scrollable ? (accountSearch[group.id] || '').trim() : '';
                          const search = searchRaw.toLowerCase();
                          const matchesSearch = (a) =>
                            a.account_code.toLowerCase().includes(search) ||
                            (a.description || '').toLowerCase().includes(search) ||
                            (a.account_type || '').toLowerCase().includes(search);
                          // A row being edited always stays visible, whatever the search filter says
                          const visible = (accounts || []).filter(a => !search || a.id === acEdit?.id || matchesSearch(a));
                          // Genuine matches only (the always-visible edit row is not counted unless it matches)
                          const matchCount = search ? (accounts || []).filter(matchesSearch).length : (accounts || []).length;
                          const typeBadge = (t) => /income/i.test(t) ? 'bg-green-50 text-green-700' : /expense/i.test(t) ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600';
                          const datalistId = `account-types-${group.id}`;
                          const preview = imp.preview;
                          const problemRows = preview ? preview.rows.filter(r => r.status === 'invalid' || r.status === 'duplicate_in_file') : [];
                          const willImport = preview ? preview.summary.new + preview.summary.update + preview.summary.unchanged : 0;
                          // Replace needs the loaded account list to know how many accounts it will delete
                          const replaceUnavailable = accounts === undefined || !!acLoadError;
                          const replaceRemoved = preview && imp.mode === 'replace' ? (getReplaceRemoved(group.id) ?? 0) : 0;
                          const replaceBlocked = imp.mode === 'replace' && replaceUnavailable;
                          const cancelAdd = () => setAccountFormState(prev => ({ ...prev, [group.id]: EMPTY_ACCOUNT_FORM }));
                          const cancelEdit = () => setAccountEditState(prev => ({ ...prev, [group.id]: null }));
                          const chip = (label, count, cls) => (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${cls}`}>
                              <span className="font-semibold">{count}</span> {label}
                            </span>
                          );
                          return (
                            <div className="border-t border-gray-200 px-4 py-3">
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <div>
                                  <h4 className="inline text-xs font-medium text-gray-700">Set of Accounts</h4>
                                  {accounts !== undefined && <span className="ml-1.5 text-xs text-gray-500">({accounts.length})</span>}
                                </div>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                  <button
                                    onClick={() => accountFileInputs.current[group.id]?.click()}
                                    disabled={imp.uploading || busy}
                                    aria-busy={imp.uploading}
                                    className={`${LINK_CLS} disabled:opacity-50`}
                                  >
                                    {imp.uploading ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiUpload className="w-3 h-3" />}
                                    {imp.uploading ? 'Reading…' : 'Import'}
                                  </button>
                                  <button onClick={() => handleDownloadAccountTemplate(group.id)} disabled={busy} className={`${LINK_CLS} disabled:opacity-50`}>
                                    <FiDownload className="w-3 h-3" />
                                    Download template
                                  </button>
                                  <button
                                    onClick={() => handleExportAccountsCsv(group)}
                                    disabled={!accounts?.length || busy}
                                    className={`${LINK_CLS} disabled:opacity-50`}
                                  >
                                    <FiDownload className="w-3 h-3" />
                                    Export CSV
                                  </button>
                                  {!acForm.adding && (
                                    <button onClick={() => setAccountFormFields(group.id, { adding: true })} disabled={busy} className={`${LINK_CLS} disabled:opacity-50`}>
                                      <FiPlus className="w-3 h-3" />
                                      Add
                                    </button>
                                  )}
                                </div>
                                <input
                                  type="file"
                                  accept=".xls,.xlsx,.csv"
                                  className="hidden"
                                  aria-label="Choose account list file to import"
                                  ref={el => { accountFileInputs.current[group.id] = el; }}
                                  onChange={e => { handleAccountFileSelected(group.id, e.target.files?.[0]); e.target.value = ''; }}
                                />
                              </div>

                              {imp.error && <p role="alert" className="text-xs text-red-600 mb-2">{imp.error}</p>}
                              {imp.result && (
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <p role="status" className="text-xs text-green-700">{imp.result}</p>
                                  <button
                                    onClick={() => setAccountImportFields(group.id, { result: '' })}
                                    aria-label="Dismiss import result"
                                    className={`p-1 text-gray-500 hover:text-gray-700 flex-shrink-0 ${FOCUS_RING}`}
                                  >
                                    <FiX className="w-3 h-3" />
                                  </button>
                                </div>
                              )}

                              {/* Import preview */}
                              {preview && (
                                <div role="region" aria-label="Import preview" className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded">
                                  <p className="text-xs text-gray-700 mb-1.5">{preview.summary.total} rows read</p>
                                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                                    {chip('new', preview.summary.new, 'bg-green-50 text-green-700')}
                                    {chip('to update', preview.summary.update, 'bg-blue-50 text-blue-700')}
                                    {chip('unchanged (already match)', preview.summary.unchanged, 'bg-gray-100 text-gray-600')}
                                    {preview.summary.duplicate_in_file > 0 && chip('duplicate in file', preview.summary.duplicate_in_file, 'bg-amber-50 text-amber-700')}
                                    {preview.summary.invalid > 0 && chip('invalid', preview.summary.invalid, 'bg-red-50 text-red-700')}
                                  </div>
                                  <p className="text-xs font-medium text-gray-800">Will be imported: {willImport}</p>
                                  {preview.warnings?.map((w, i) => (
                                    <p key={i} className="text-xs text-amber-700 mt-1">{w}</p>
                                  ))}
                                  {problemRows.length > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs font-medium text-gray-700">Rows with problems (not imported)</p>
                                      <ul className="mt-0.5 text-xs">
                                        {problemRows.slice(0, 5).map(r => (
                                          <li key={r.row_number} className={r.status === 'invalid' ? 'text-red-600' : 'text-amber-700'}>
                                            <span className="font-mono">Row {r.row_number}</span>: {r.message}
                                          </li>
                                        ))}
                                        {problemRows.length > 5 && <li className="text-gray-500">…and {problemRows.length - 5} more</li>}
                                      </ul>
                                    </div>
                                  )}
                                  <fieldset disabled={busy} className="mt-2 min-w-0 border-0 p-0 m-0">
                                    <legend className="sr-only">Import mode</legend>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-700">
                                      <label className="inline-flex items-center gap-1 cursor-pointer">
                                        <input
                                          type="radio"
                                          name={`account-import-mode-${group.id}`}
                                          checked={imp.mode === 'merge'}
                                          onChange={() => setAccountImportFields(group.id, { mode: 'merge' })}
                                        />
                                        Merge (add and update, keep others)
                                      </label>
                                      <label className="inline-flex items-center gap-1 cursor-pointer">
                                        <input
                                          type="radio"
                                          name={`account-import-mode-${group.id}`}
                                          checked={imp.mode === 'replace'}
                                          disabled={replaceUnavailable}
                                          onChange={() => setAccountImportFields(group.id, { mode: 'replace' })}
                                        />
                                        Replace (delete accounts not in file)
                                      </label>
                                    </div>
                                  </fieldset>
                                  {replaceUnavailable && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {acLoadError
                                        ? 'Replace is unavailable because the account list could not be loaded. Retry loading, or use Merge.'
                                        : 'Replace is unavailable until the account list has loaded. Merge can be used now.'}
                                    </p>
                                  )}
                                  {imp.mode === 'replace' && !replaceUnavailable && replaceRemoved > 0 && (
                                    <p className="text-xs text-red-600 mt-1">
                                      {replaceRemoved} existing account{replaceRemoved === 1 ? '' : 's'} not in this file will be permanently deleted.
                                    </p>
                                  )}
                                  <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <button
                                      onClick={() => handleCommitAccountImport(group.id)}
                                      disabled={busy || willImport === 0 || replaceBlocked}
                                      aria-busy={busy}
                                      className={`inline-flex items-center gap-1 px-2 py-1 min-h-[44px] md:min-h-0 text-xs text-white rounded disabled:opacity-50 ${FOCUS_RING} ${
                                        imp.mode === 'replace' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                                      }`}
                                    >
                                      {busy ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiCheck className="w-3 h-3" />}
                                      {imp.mode === 'replace'
                                        ? (replaceBlocked ? 'Replace unavailable' : replaceRemoved > 0 ? `Replace and delete ${replaceRemoved}` : 'Confirm replace')
                                        : 'Confirm import'}
                                    </button>
                                    <button
                                      onClick={() => setAccountImportFields(group.id, { preview: null })}
                                      disabled={busy}
                                      className={`px-2 py-1 min-h-[44px] md:min-h-0 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 ${FOCUS_RING}`}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}

                              {accounts === undefined && !acLoadError && (
                                <p className="text-xs text-gray-500 italic">Loading accounts…</p>
                              )}
                              {acLoadError && (
                                <p role="alert" className="text-xs text-red-600 mb-2">
                                  Couldn't load accounts.{' '}
                                  <button onClick={() => loadAccounts(group.id)} className={`underline text-blue-700 hover:text-blue-800 ${FOCUS_RING}`}>Retry</button>
                                </p>
                              )}
                              {accounts?.length === 0 && !acForm.adding && (
                                <p className="text-xs text-gray-500 italic">No accounts yet. Import a spreadsheet or add accounts manually.</p>
                              )}
                              {scrollable && (
                                <input
                                  type="search"
                                  value={accountSearch[group.id] || ''}
                                  onChange={e => setAccountSearch(prev => ({ ...prev, [group.id]: e.target.value }))}
                                  placeholder="Search accounts…"
                                  aria-label="Search accounts"
                                  className={`w-full mb-1 ${INPUT_CLS}`}
                                />
                              )}
                              {scrollable && searchRaw && (
                                <p className="text-xs text-gray-500 mb-2">
                                  Showing {matchCount} of {accounts.length}{' '}
                                  <button
                                    onClick={() => setAccountSearch(prev => ({ ...prev, [group.id]: '' }))}
                                    className={`underline text-blue-700 hover:text-blue-800 ${FOCUS_RING}`}
                                  >
                                    Clear
                                  </button>
                                </p>
                              )}
                              {accounts?.length > 0 && (
                                <div
                                  role="region"
                                  aria-label="Accounts list"
                                  tabIndex={scrollable ? 0 : undefined}
                                  className={`mb-2 ${scrollable ? `max-h-64 overflow-y-auto ${FOCUS_RING}` : ''}`}
                                >
                                <ul className="space-y-1">
                                  {visible.map(a => (
                                    acEdit?.id === a.id ? (
                                      <li key={a.id} className="py-1">
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                          <input
                                            type="text"
                                            value={acEdit.code}
                                            onChange={e => setAccountEditState(prev => ({ ...prev, [group.id]: { ...acEdit, code: e.target.value } }))}
                                            autoFocus
                                            placeholder="Code"
                                            aria-label="Account code"
                                            className={`w-full sm:w-28 font-mono ${INPUT_CLS}`}
                                            onKeyDown={e => { if (e.key === 'Enter') handleSaveAccountEdit(group.id); if (e.key === 'Escape') cancelEdit(); }}
                                          />
                                          <input
                                            type="text"
                                            value={acEdit.description}
                                            onChange={e => setAccountEditState(prev => ({ ...prev, [group.id]: { ...acEdit, description: e.target.value } }))}
                                            placeholder="Description"
                                            aria-label="Description"
                                            className={`w-full sm:w-auto sm:flex-1 ${INPUT_CLS}`}
                                            onKeyDown={e => { if (e.key === 'Enter') handleSaveAccountEdit(group.id); if (e.key === 'Escape') cancelEdit(); }}
                                          />
                                          <input
                                            type="text"
                                            list={datalistId}
                                            value={acEdit.type}
                                            onChange={e => setAccountEditState(prev => ({ ...prev, [group.id]: { ...acEdit, type: e.target.value } }))}
                                            placeholder="Type"
                                            aria-label="Account type"
                                            className={`w-full sm:w-36 ${INPUT_CLS}`}
                                            onKeyDown={e => { if (e.key === 'Enter') handleSaveAccountEdit(group.id); if (e.key === 'Escape') cancelEdit(); }}
                                          />
                                          <div className="flex items-center gap-2">
                                            <button
                                              onClick={() => handleSaveAccountEdit(group.id)}
                                              disabled={acEdit.saving || busy || !acEdit.code.trim()}
                                              aria-label="Save account"
                                              title="Save account"
                                              className={`p-1.5 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 inline-flex items-center justify-center text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex-shrink-0 ${FOCUS_RING}`}
                                            >
                                              {acEdit.saving ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiCheck className="w-3 h-3" />}
                                            </button>
                                            <button
                                              onClick={cancelEdit}
                                              aria-label="Cancel edit"
                                              title="Cancel edit"
                                              className={`p-1.5 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 inline-flex items-center justify-center text-gray-500 hover:text-gray-700 flex-shrink-0 ${FOCUS_RING}`}
                                            >
                                              <FiX className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                        {acEdit.error && <p role="alert" className="text-xs text-red-600 mt-1">{acEdit.error}</p>}
                                      </li>
                                    ) : (
                                      <li key={a.id} className="py-1 group text-xs">
                                        <div className="flex items-start gap-2">
                                          <span className="w-28 flex-shrink-0 text-gray-800 font-mono break-all">{a.account_code}</span>
                                          <span className="flex-1 min-w-0 text-gray-600 break-words">{a.description}</span>
                                          {a.account_type && (
                                            <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-xs ${typeBadge(a.account_type)}`}>{a.account_type}</span>
                                          )}
                                          <button
                                            onClick={() => setAccountEditState(prev => ({ ...prev, [group.id]: { id: a.id, code: a.account_code, description: a.description || '', type: a.account_type || '', saving: false, error: '' } }))}
                                            disabled={busy}
                                            aria-label={`Edit account ${a.account_code}`}
                                            className={`${ROW_ICON_BTN} text-gray-500 hover:text-blue-600`}
                                            title="Edit account"
                                          >
                                            <FiEdit2 className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteAccount(group.id, a)}
                                            disabled={busy}
                                            aria-label={`Delete account ${a.account_code}`}
                                            className={`${ROW_ICON_BTN} text-red-600 hover:text-red-700`}
                                            title="Delete account"
                                          >
                                            <FiTrash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                        {acRowError?.id === a.id && <p role="alert" className="text-xs text-red-600 mt-1">{acRowError.message}</p>}
                                      </li>
                                    )
                                  ))}
                                  {matchCount === 0 && (
                                    <li className="text-xs text-gray-500 italic py-1">No accounts match your search.</li>
                                  )}
                                </ul>
                                </div>
                              )}

                              {acForm.adding && (
                                <div className="mt-1">
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                    <input
                                      type="text"
                                      value={acForm.code}
                                      onChange={e => setAccountFormFields(group.id, { code: e.target.value })}
                                      placeholder="Account code"
                                      aria-label="Account code"
                                      autoFocus
                                      className={`w-full sm:w-28 font-mono ${INPUT_CLS}`}
                                      onKeyDown={e => { if (e.key === 'Enter') handleAddAccount(group.id); if (e.key === 'Escape') cancelAdd(); }}
                                    />
                                    <input
                                      type="text"
                                      value={acForm.description}
                                      onChange={e => setAccountFormFields(group.id, { description: e.target.value })}
                                      placeholder="Description"
                                      aria-label="Description"
                                      className={`w-full sm:w-auto sm:flex-1 ${INPUT_CLS}`}
                                      onKeyDown={e => { if (e.key === 'Enter') handleAddAccount(group.id); if (e.key === 'Escape') cancelAdd(); }}
                                    />
                                    <input
                                      type="text"
                                      list={datalistId}
                                      value={acForm.type}
                                      onChange={e => setAccountFormFields(group.id, { type: e.target.value })}
                                      placeholder="Account type"
                                      aria-label="Account type"
                                      className={`w-full sm:w-36 ${INPUT_CLS}`}
                                      onKeyDown={e => { if (e.key === 'Enter') handleAddAccount(group.id); if (e.key === 'Escape') cancelAdd(); }}
                                    />
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleAddAccount(group.id)}
                                        disabled={acForm.saving || !acForm.code.trim()}
                                        aria-label="Add account"
                                        title="Add account"
                                        className={`p-1.5 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 inline-flex items-center justify-center text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex-shrink-0 ${FOCUS_RING}`}
                                      >
                                        {acForm.saving ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiCheck className="w-3 h-3" />}
                                      </button>
                                      <button
                                        onClick={cancelAdd}
                                        aria-label="Cancel add"
                                        title="Cancel add"
                                        className={`p-1.5 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 inline-flex items-center justify-center text-gray-500 hover:text-gray-700 flex-shrink-0 ${FOCUS_RING}`}
                                      >
                                        <FiX className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  {acForm.error && <p role="alert" className="text-xs text-red-600 mt-1">{acForm.error}</p>}
                                </div>
                              )}

                              <datalist id={datalistId}>
                                {ACCOUNT_TYPE_SUGGESTIONS.map(t => <option key={t} value={t} />)}
                              </datalist>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Document Types */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-medium text-gray-900">Document Types</h3>
            <p className="text-xs text-gray-500 mt-0.5">Optional descriptions to help the AI identify each document type during extraction.</p>
          </div>
          {!editingDocTypes && (
            <button
              onClick={handleEditDocTypes}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiEdit2 className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {SUPPLIER_DOC_TYPES.map(t => (
            <div key={t.value} className="flex items-start gap-3 px-4 py-3">
              <span className={`mt-0.5 text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${typeColor(t.value)}`}>{t.label}</span>
              <div className="flex-1 min-w-0">
                {editingDocTypes ? (
                  <input
                    type="text"
                    value={docTypeEdits[t.value] || ''}
                    onChange={e => setDocTypeEdits(prev => ({ ...prev, [t.value]: e.target.value }))}
                    placeholder={DOC_TYPE_PLACEHOLDERS[t.value] || 'Describe this document type...'}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500 text-gray-700"
                  />
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {docTypeDescriptions[t.value] || <span className="text-gray-300">No description set</span>}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {editingDocTypes && (
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={handleCancelDocTypes}
              disabled={savingDocTypes}
              className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveDocTypes}
              disabled={savingDocTypes}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {savingDocTypes ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheck className="w-3.5 h-3.5" />}
              Save
            </button>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-green-900 mb-2">About Recon Groups</h4>
        <ul className="text-sm text-green-800 space-y-1">
          <li>• Create one group per supplier (or set of related suppliers)</li>
          <li>• Each group holds the SharePoint, Exchange, and Doxolve folders for that supplier</li>
          <li>• When creating a recon run, select the group to sync — only that group's folders are used</li>
          <li>• Standard document types: {SUPPLIER_DOC_TYPES.map(t => t.label).join(', ')}</li>
          <li>• Custom types (per group): add extra types like "Delivery Note" — they appear in documents but don't affect financial matching</li>
        </ul>
      </div>

      {/* Delete group confirm modal */}
      {confirmDeleteGroup && (
        <DeleteGroupConfirm
          group={confirmDeleteGroup}
          onConfirm={handleDeleteGroup}
          onCancel={() => setConfirmDeleteGroup(null)}
          deleting={deletingGroupId === confirmDeleteGroup.id}
        />
      )}
    </div>
  );
}
